<script setup lang="ts">
import QuestionMarkIcon from '@/icons/QuestionMarkIcon.vue';
import { ref } from 'vue';
import BaseButton from '../Buttons/BaseButton.vue';
import Badge from 'primevue/badge';

  defineProps({
    // Main Card Wrapper
    className: {
      type: String,
      required: false,
      default: '',
    },

    // Header Props
    title: {
      type: String,
      required: false,
      default: '',
    },
    titleClass: {
      type: String,
      required: false,
      default: '',
    },
    subTitle: {
      type: String,
      required: false,
      default: ''
    },
    subTitleClass: {
      type: String,
      required: false,
      default: ''
    },
    headerClass: {
      type: String,
      required: false,
      default: '',
    },
    showHeaderBorder: {
      type: Boolean,
      required: false,
      default: false,
    },
    showHeaderPaddingBottom: {
      type: Boolean,
      required: false,
      default: false,
    },
    headerIcon: {
      type: Object,
      required: false,
      default: null,
    },

    // Body Props
    bodyClass: {
      type: String,
      required: false,
      default: '',
    },
    noBodyPadding: {
      type: Boolean,
      default: false,
    },

    // Footer Props
    footerClass: {
      type: String,
      required: false,
      default: '',
    },
    hideFooterBorder: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideFooterPaddingX: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideFooterPaddingTop: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Help Popup Props (New)
    helpContent: {
      type: String,
      required: false,
      default: '',
    },
    showHelpIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    helpTitle: {
      type: String,
      required: false,
      default: 'Help Information',
    },
    allowHtml: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Top Right Badge Props
    badgeLabel: {
      type: String,
      required: false,
      default: '',
    },
    badgeClass: {
      type: String,
      required: false,
      default: '',
    },
  });

  const showHelpPopup = ref(false);

  // Handle actions from help content
  interface HelpActionData {
    text?: string;
  }
  const handleHelpAction = (action: string, data?: HelpActionData) => {
    console.log('Help action:', action, data);

    switch (action) {
      case 'open-docs':
        window.open('/documentation', '_blank');
        break;
      case 'contact-support':
        window.location.href = '/support';
        break;
      case 'watch-tutorial':
        window.open('https://youtube.com/tutorial', '_blank');
        break;
      case 'copy-text':
        navigator.clipboard.writeText(data?.text ?? '');
        break;
      default:
        console.log('Unknown action:', action);
    }
  };
</script>

<template>
  <div :class="['card-wrapper-main relative', className]">

    <!-- Header -->
    <template v-if="$slots.header || title">
      <div
        :class="[
          'this-card-header flex items-center gap-6 pt-4 px-4',
          headerClass,
          showHeaderBorder ? 'border-b border-stroke border-gray-200 dark:border-gray-800' : '',
          showHeaderPaddingBottom ? 'pb-4' : ''
        ]"
      >
        <h4
          v-if="title"
          :class="['flex-1 flex items-center text-lg font-medium gap-2', titleClass]"
        >
          <!-- SVG Icon Component -->
          <component
            v-if="headerIcon"
            :is="headerIcon"
            class="w-7 h-7"
            aria-hidden="true"
          />
          <p class="flex flex-col">
          {{ title }}
              <!-- Note -->
          <span
            v-if="subTitle"
            v-html="subTitle"
            :class="['text-sm font-normal', subTitleClass]"
          ></span>
          </p>
        </h4>
        <slot name="header" />
      </div>
    </template>

    <!-- Body -->
    <div :class="['this-card-body flex', !noBodyPadding ? 'p-4' : '', bodyClass]">
      <slot name="body" />
    </div>

    <!-- Footer -->
    <template v-if="$slots.footer">
      <div
        :class="[
          'this-card-footer pb-4',
          hideFooterPaddingX ? '' : 'px-4',
          hideFooterPaddingTop ? '' : 'pt-4',
          hideFooterBorder ? '' : 'border-t border-gray-200 dark:border-gray-800 flex', footerClass
        ]"
      >
        <slot name="footer" />
      </div>
    </template>

    <Badge
      v-if="badgeLabel"
      :value="badgeLabel"
      :class="['absolute -right-9 -top-3 flex h-12 w-24 rotate-45 items-end justify-center px-4 py-1.5 text-sm font-medium text-white shadow-theme-xs', badgeClass]"
    />

    <!-- Help Icon (Bottom Right) -->
    <button
      @click="showHelpPopup = true"
      v-if="showHelpIcon || $slots.help"
      class="flex items-center justify-center w-6 h-6 bg-gray-200 hover:bg-gray-300 dark:bg-white/10 dark:hover:bg-white/20 text-gray-700 dark:text-white/90 hover:text-gray-800 transition-colors duration-200 focus:outline-none absolute rounded-tl-sm rounded-br-md -right-0 -bottom-0 text-sm font-medium shadow-theme-xs"
      aria-label="Help information"
    >
      <QuestionMarkIcon class="w-4 h-4" />
    </button>

      <!-- Help Popup (Centered within card) -->
      <div
        v-if="showHelpPopup"
        class="absolute inset-0 z-10 flex items-center justify-center p-4"
      >
        <!-- Backdrop overlay inside card -->
        <div
          class="absolute inset-0 bg-gray-400/50 backdrop-blur-[25px] rounded-md"
          @click="showHelpPopup = false"
        ></div>

        <!-- Popup Card -->
        <div
          class="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-sm w-full max-h-96 overflow-hidden transform transition-all duration-300 scale-100 border border-gray-200 dark:border-gray-700"
          @click.stop
        >
          <!-- Popup Header -->
          <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ helpTitle }}
            </h3>
            <button
              @click="showHelpPopup = false"
              class="flex items-center justify-center w-6 h-6 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close help popup"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Popup Content -->
          <div class="p-4 overflow-y-auto max-h-64">
            <!-- Slot with actions support -->
            <div v-if="$slots.help" class="text-sm text-gray-700 dark:text-gray-300 space-y-3">
              <slot name="help" :handleAction="handleHelpAction" />
            </div>

            <!-- HTML Content with interactive elements -->
            <div v-else-if="helpContent && allowHtml" class="text-sm text-gray-700 dark:text-gray-300 space-y-3" v-html="helpContent">
            </div>

            <!-- Plain text content -->
            <div v-else-if="helpContent" class="text-sm text-gray-700 dark:text-gray-300 space-y-3">
              {{ helpContent }}
            </div>

            <div v-else class="text-gray-500 dark:text-gray-400 text-center py-4 text-sm">
              No help content available.
            </div>
          </div>

          <!-- Popup Footer -->
          <div class="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
            <BaseButton
              text="Close"
              variant="secondary"
              size="xs"
              @click="showHelpPopup = false"
            />
          </div>
        </div>
      </div>

  </div>
</template>

<!-- Example usage of card component -->

<!-- <Card
  title="User Profile"
  :headerIcon="UserIcon" // Pass SVG component
  className="shadow-lg custom-bg"
  headerClass="bg-gray-100 justify-between"
  titleClass="text-lg text-primary"
  bodyClass="grid grid-cols-2 gap-4"
  footerClass="justify-end bg-gray-50"
  :showHeaderBorder="true"
  :showHeaderPaddingBottom="true"
>
  <template #header>
    <button class="btn-sm">Edit</button>
  </template>

  <template #body>
    <div>Name: John Doe</div>
    <div>Email: john@example.com</div>
  </template>

  <template #footer>
    <button class="btn btn-primary">Save</button>
  </template>
</Card> -->

<!-- explanation of class name usage: -->

<!-- className="shadow-lg custom-bg" → added to the main wrapper.

headerClass="bg-gray-100 justify-between" → extends the header section.

titleClass="text-lg text-primary" → customizes title text styling inside the header.

bodyClass="grid grid-cols-2 gap-4" → applies grid layout to the body.

footerClass="justify-end bg-gray-50" → aligns footer content to the right with a background. -->
